import React, { useId, useState, type ComponentType } from "react";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  size,
  useFloating,
} from "@floating-ui/react";
import { Combobox, Transition } from "@headlessui/react";
import { type Noop, type RefCallBack } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twJoin, twMerge } from "tailwind-merge";
import { IconButton } from "@components/IconButton/IconButton";
import {
  ChevronIcon,
  CrossIcon,
  ExclamationCircle20Icon,
  LoaderIcon,
} from "@components/icons";
import { Label } from "@components/Label/Label";

type TValue = string | number;
type TLabel = string | number;

type TAutocompleteOption = {
  /**
   * Option value.
   */
  value: TValue;
  /**
   * Option label.
   */
  label: TLabel;
  /**
   * Disabled option flag.
   */
  disabled?: boolean;
  /**
   * Option icon element.
   */
  icon?: ComponentType<React.SVGProps<SVGSVGElement>>;
};

export interface IAutocompleteProps {
  /**
   * Available options array.
   */
  options: TAutocompleteOption[];
  /**
   * Selected option value.
   */
  value?: TValue | Partial<TAutocompleteOption>;
  /**
   * Autocomplete field label.
   */
  label?: string;
  /**
   * Select box placeholder.
   */
  placeholder?: string;
  /**
   * Value change callback.
   */
  onChange?: (selected?: TValue | Partial<TAutocompleteOption>) => void;
  /**
   * Field blur callback.
   */
  onBlur?: Noop;
  /**
   * Disabled field flag.
   */
  disabled?: boolean;
  /**
   * Field error text.
   */
  error?: string;
  /**
   * Autocomplete field size.
   */
  height?: "extra-small" | "small" | "medium";
  /**
   * Field hint text.
   */
  hint?: string;
  /**
   * Ref.
   */
  inputRef?: RefCallBack;
  /**
   * Input field icon.
   */
  icon?: ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Container's class name.
   */
  className?: string;
  /**
   * Input's class name.
   */
  inputClassName?: string;
  /**
   * Option's class name.
   */
  optionClassName?: string;
  /**
   * Autocomplete loading state flag.
   */
  isLoading?: boolean;
  /**
   * Whether autocomplete content should stay in fixed position.
   */
  isPositionFixed?: boolean;
  /**
   * Whether autocomplete should allow enter custom value. If enabled, onChange will return Partial<TAutocompleteOption>.
   */
  isCustomValueMode?: boolean;
  // onQueryChange?: (query: string) => TAutocompleteOption[];
  /**
   * Query change callback.
   */
  onQueryChange?: (query: string) => void;
  /**
   * Whether cross icon should be displayed for clearing autocomplete entry.
   */
  isClearEnabled?: boolean;
  /**
   * Clear callback.
   */
  onClear?: () => void;
  /**
   * Whether options should be truncated.
   */
  shouldTruncateOptions?: boolean;
}

export const Autocomplete: React.FC<IAutocompleteProps> = ({
  options,
  value,
  label,
  placeholder,
  onChange,
  onBlur,
  disabled,
  error,
  hint,
  height = "medium",
  inputRef,
  icon: Icon,
  className,
  inputClassName,
  optionClassName,
  isLoading,
  isPositionFixed,
  isCustomValueMode = false,
  onQueryChange,
  isClearEnabled = false,
  onClear,
  shouldTruncateOptions = true,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState<string>("");
  const selectedOptionLabel = isCustomValueMode
    ? (value as TAutocompleteOption)?.label
    : options.find((o) => o.value === value)?.label;
  const inputId = useId();

  const setQueryValue = (value: string) => {
    setQuery(value);
    onQueryChange?.(value);
  };

  const parseString = (input: string | number): string => {
    return typeof input === typeof 0 ? input.toString() : (input as string);
  };

  const filteredOptions =
    query === ""
      ? // This library can only handle like 200 items in a list
        options.slice(0, 100)
      : options
          .filter((option) =>
            parseString(option.label)
              .toLowerCase()
              .replace(/\s+/g, "")
              .includes(query.toLowerCase().replace(/\s+/g, "")),
          )
          .slice(0, 100);

  const { refs, floatingStyles } = useFloating({
    strategy: isPositionFixed ? "fixed" : "absolute",
    middleware: [
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
      flip(),
    ],
    whileElementsMounted: autoUpdate,
  });

  return (
    <div className={twMerge("flex flex-col gap-1.5", className)}>
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <Combobox
        // eslint-disable-next-line
        value={value || null}
        onChange={(value) => onChange?.(value ?? undefined)}
        // eslint-disable-next-line
        disabled={disabled || isLoading}
        nullable
      >
        {({ open, disabled: inputDisabled }) => (
          <div className="relative">
            <div
              ref={refs.setReference}
              className="relative w-full cursor-default bg-white text-left"
            >
              <Combobox.Button className="w-full">
                {Icon && (
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4 text-grey-dark">
                    <Icon />
                  </div>
                )}
                <Combobox.Input
                  id={inputId}
                  ref={inputRef}
                  className={twMerge(
                    "h-11 w-full rounded-full border border-grey-hover py-2.5 pl-3.5 pr-9 text-base-black transition-all placeholder:text-grey-dark focus:border-green-dark focus:outline-none focus:ring-4 focus:ring-green-dark focus:ring-opacity-10 disabled:bg-base-light-grey",
                    height === "extra-small" && "h-9 py-2",
                    height === "small" && "h-10 py-2",
                    open &&
                      "border-green-dark ring-4 ring-green-dark ring-opacity-10",
                    inputDisabled &&
                      "bg-base-light-grey text-grey-dark active:ring-0",
                    error &&
                      "border-red-dark pr-16 ring-red-dark focus:border-red-dark focus:ring-red-dark",
                    Icon && "pl-12",
                    inputClassName,
                  )}
                  displayValue={() =>
                    parseString(open ? "" : (selectedOptionLabel ?? ""))
                  }
                  placeholder={placeholder}
                  onChange={(event) => setQueryValue(event.target.value.trim())}
                  onBlur={() => onBlur?.()}
                />
                {isLoading ? (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <LoaderIcon className="h-5 w-5 animate-spin text-grey-dark" />
                  </div>
                ) : (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    {selectedOptionLabel && isClearEnabled ? (
                      <IconButton
                        type="button"
                        iconClassName="size-5"
                        icon={CrossIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          onClear?.();
                          onChange?.(undefined);
                        }}
                        aria-label={t("action.clear")}
                      />
                    ) : (
                      <ChevronIcon
                        className={twJoin(
                          "h-5 w-5 text-grey-dark transition-transform duration-100",
                          open && "rotate-180",
                        )}
                      />
                    )}
                  </div>
                )}
              </Combobox.Button>
              {error && (
                <div className="absolute inset-y-0 right-9 flex items-center text-red-dark">
                  <ExclamationCircle20Icon />
                </div>
              )}
            </div>
            <Transition
              as="div"
              enter="transition ease-out duration-100 scale-100"
              enterFrom="opacity-0 scale-[.98]"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-100 scale-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-[.98]"
              afterLeave={() => setQueryValue("")}
            >
              <FloatingPortal>
                <Combobox.Options
                  ref={refs.setFloating}
                  style={floatingStyles}
                  className="pointer-events-auto absolute z-[9999] mt-2 max-h-60 w-full overflow-hidden rounded-xl border border-grey-light bg-base-white p-0 text-base text-base-black shadow-lg focus:outline-none"
                >
                  <div className="flex max-h-60 flex-col gap-1 overflow-auto rounded-lg p-2">
                    {!isCustomValueMode &&
                    ((!filteredOptions.length && query !== "") ||
                      !options.length) ? (
                      <div className="relative cursor-default select-none px-4 py-2 text-base-black">
                        {t("common.noResults")}
                      </div>
                    ) : (
                      <React.Fragment>
                        {isCustomValueMode && query.length > 0 && (
                          <Combobox.Option
                            className={({ active }) =>
                              twMerge(
                                "relative flex select-none flex-row items-center rounded-full px-4 py-2",
                                query === value &&
                                  "bg-blue-light text-blue-dark",
                                active && "bg-base-light-grey",
                                !shouldTruncateOptions && "rounded-3xl",
                              )
                            }
                            value={{ label: query }}
                          >
                            <span
                              className={twMerge(
                                "font-normal",
                                query === value && "font-medium",
                                shouldTruncateOptions && "truncate",
                              )}
                            >
                              {query}
                            </span>
                          </Combobox.Option>
                        )}
                        {filteredOptions.map((option) => (
                          <Combobox.Option
                            key={option.value}
                            className={({ active }) =>
                              twMerge(
                                "relative flex select-none flex-row items-center rounded-full px-4 py-2",
                                !option.disabled && "cursor-pointer",
                                option.value === value &&
                                  "bg-blue-light text-blue-dark",
                                active && "bg-base-light-grey",
                                option.icon && "gap-2",
                                option.disabled && "bg-none text-grey-hover",
                                !shouldTruncateOptions && "rounded-3xl",
                              )
                            }
                            disabled={option.disabled}
                            value={
                              isCustomValueMode
                                ? { value: option.value, label: option.label }
                                : option.value
                            }
                          >
                            {() => (
                              <>
                                {option.icon &&
                                  React.createElement(option.icon, {
                                    className: twMerge(
                                      "w-4 flex-shrink-0",
                                      option.value === value &&
                                        "text-blue-dark",
                                      option.disabled && "text-grey-hover",
                                    ),
                                  })}
                                <span
                                  className={twMerge(
                                    "font-normal",
                                    option.value === value && "font-medium",
                                    optionClassName,
                                    shouldTruncateOptions && "truncate",
                                  )}
                                >
                                  {option.label}
                                </span>
                              </>
                            )}
                          </Combobox.Option>
                        ))}
                      </React.Fragment>
                    )}
                  </div>
                </Combobox.Options>
              </FloatingPortal>
            </Transition>
          </div>
        )}
      </Combobox>
      {hint && !error && <span className="text-sm text-grey-dark">{hint}</span>}
      {error && (
        <span className="break-all text-sm text-red-dark">{error}</span>
      )}
    </div>
  );
};
